import { types as at, errorAction, successAction } from "@authzsvc/api-lib";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGroupById,
  transferGroupOwner,
  updateGroup,
} from "../../actions/api";
import { isApiCallLoading } from "../../selectors";
import GroupEditForm from "./GroupEditForm";
import GroupScopesSelector from "./scopes/GroupScopesSelector";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "../../util";
import EGroupWarning from "./EGroupWarning";
import InternalStateWarning from "./InternalStateWarning";

/**
 * Clones the group and it updates it with all the fields passed
 * @param {object} group
 * @param {object} updated
 */
const updateGroupObject = (group, updated) => {
  let newGroup = { ...group };
  Object.keys(updated).forEach((key) => {
    newGroup[key] = updated[key];
  });
  return newGroup;
};

const GroupDetails = ({
  canManageGroup,
  isDisabledByInternalState,
  isReader,
  userCategory,
  managedButMembershipModifiable,
  isAdmin,
}) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const selectedGroup = useSelector((state) => state.groups.selectedGroup);
  const groupMemberships = useSelector((state) => state.groupMembership.direct);
  const globalLoading = useSelector(
    (state) =>
      isApiCallLoading(state, at.GET_GROUP_BY_ID) ||
      isApiCallLoading(state, at.GET_GROUPS_I_OWN)
  );

  const handleUpdate = async (values) => {
    setLoading(true);
    const updatedGroup = updateGroupObject(selectedGroup, values);
    const result = await dispatch(updateGroup(updatedGroup.id, updatedGroup));

    if (result.type === errorAction(at.UPDATE_GROUP)) {
      showErrorNotification(result.payload.title, result.payload.data.message);
    }

    await dispatch(selectGroupById(updatedGroup.id));
    setLoading(false);
  };

  const handleSelectNewOwner = async (owner) => {
    if (!owner) return;
    setLoading(true);

    const response = await dispatch(
      transferGroupOwner(selectedGroup.id, owner.id)
    );

    if (response.type === successAction(at.TRANSFER_GROUP_OWNER)) {
      if (response.payload.data.requestorId) {
        showWarningNotification(
          "Approval required by new administrator",
          "The owner or administrators of the group have been notified."
        );
      } else {
        showSuccessNotification("", "Ownership has been updated successfully!");

        await dispatch(selectGroupById(selectedGroup.id));
      }
    } else if (response.type === errorAction(at.TRANSFER_GROUP_OWNER)) {
      const title = response?.payload?.title || "Sorry, an error occurred";
      const description =
        response?.payload?.data?.message || "Please try again later";

      showErrorNotification(title, description);
    }

    setLoading(false);
  };

  const warningStates = ["ActionRequired", "Blocked", "Archived", "Deleted"];
  const isInternalWarning = warningStates.includes(selectedGroup.internalState);

  return (
    <>
      <EGroupWarning
        selectedGroup={selectedGroup}
        canManageGroup={canManageGroup}
        showSyncMessage={false}
      />
      {isInternalWarning && (
        <InternalStateWarning selectedGroup={selectedGroup} />
      )}
      <GroupEditForm
        groupMemberships={groupMemberships}
        dynamic={selectedGroup.dynamic || false}
        onSelectOwner={handleSelectNewOwner}
        group={selectedGroup}
        handleSubmit={handleUpdate}
        loading={loading || globalLoading}
        canManage={canManageGroup}
        isDisabledByInternalState={isDisabledByInternalState}
        isReader={isReader}
        userCategory={userCategory}
        managedButMembershipModifiable={managedButMembershipModifiable}
        isAdmin={isAdmin}
      />

      {canManageGroup && selectedGroup.id && (
        <GroupScopesSelector groupId={selectedGroup.id} />
      )}
    </>
  );
};

export default GroupDetails;
